import React from 'react';
import { motion } from 'framer-motion';
import '../../pages/TMFProject.css';

const MethodsSection: React.FC = () => {
  return (
    <section className="tmf-methods">
      <div className="tmf-container">
        {/* Section Header */}
        <motion.div
          className="tmf-section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="tmf-section-title">How We Dug Deeper</h2>
          <p className="tmf-section-intro">
            Observation showed us the problem. But we needed to go deeper. We needed methods that would meet people where they are, literally and emotionally. Our team brainstormed 15 different research methods. Five for each strategy: rapid interviews, on-site engagement, and focus groups. We presented them to Southeast Community Services.
          </p>
        </motion.div>

        {/* Presentation Gallery */}
        <motion.div
          className="tmf-presentation-gallery-wrapper"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="tmf-presentation-gallery">
            <motion.div
              className="tmf-presentation-item"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img
                src="/content/tmf data/sec presenation img/SEC Presentation.jpg"
                alt="Presenting research methods to Southeast Community Services"
              />
            </motion.div>

            <motion.div
              className="tmf-presentation-item"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <img
                src="/content/tmf data/sec presenation img/SEC Presentation (1).jpg"
                alt="Discussion with Southeast Community Services team"
              />
            </motion.div>

            <motion.div
              className="tmf-presentation-item tmf-presentation-video"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              >
                <source src="/content/tmf data/sec presenation img/IMG_1821.MOV" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p className="tmf-dog-caption">
                Say hi to Buddy, he's a rescue at Southeast Community Services 🐾
              </p>
            </motion.div>

            <motion.div
              className="tmf-presentation-item"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              >
                <source src="https://pub-8a575460bea442e2a9d816d772948be2.r2.dev/tmf-sec-presentation.mov" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </motion.div>

            <motion.div
              className="tmf-presentation-item"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              >
                <source src="/content/tmf data/sec presenation img/IMG_1825 2.MOV" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </motion.div>
          </div>
        </motion.div>

        {/* SECS Feedback Section - Redesigned */}
        <motion.div
          className="tmf-evaluation-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="tmf-evaluation-header">
            <h3>Evaluating with SECS</h3>
            <p className="tmf-evaluation-intro">
              Southeast Community Services reviewed our 15 methods and provided feedback on what would actually work in their environment. They raised practical concerns: vandalism risk, literacy and language barriers, power dynamics, time constraints, and the reality that their clients are focused on immediate needs, not research participation.
            </p>
          </div>

          {/* 8 Criteria Grid */}
          <div className="tmf-evaluation-grid">
            <motion.div
              className="tmf-eval-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <svg className="tmf-eval-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 4.5a2.5 2.5 0 0 0-2.5 2.5c0 .74.44 1.35 1.04 1.72M12 4.5a2.5 2.5 0 0 1 2.5 2.5c0 .74-.44 1.35-1.04 1.72M12 4.5V3m0 5.72V13m0 0l-3 3m3-3l3 3M5 17h14M5 20h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <h4>Accessibility</h4>
              <p>Visual & audio inclusive, minimal text, low tech</p>
            </motion.div>

            <motion.div
              className="tmf-eval-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              viewport={{ once: true }}
            >
              <svg className="tmf-eval-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h4>Emotional Safety</h4>
              <p>Voluntary, non-therapeutic, respect privacy & consent</p>
            </motion.div>

            <motion.div
              className="tmf-eval-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <svg className="tmf-eval-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <h4>Engagement</h4>
              <p>Playful but meaningful, non-transactional incentives</p>
            </motion.div>

            <motion.div
              className="tmf-eval-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.25 }}
              viewport={{ once: true }}
            >
              <svg className="tmf-eval-icon" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <h4>Time Fit</h4>
              <p>Quick to complete, adaptable to pantry flow</p>
            </motion.div>

            <motion.div
              className="tmf-eval-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <svg className="tmf-eval-icon" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 2v3m0 14v3m10-10h-3M5 12H2m16.95-6.95l-2.12 2.12M8.17 16.83l-2.12 2.12m12.9 0l-2.12-2.12M8.17 7.17L6.05 5.05" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <h4>Clarity</h4>
              <p>Simple instructions, facilitator examples, relatable framing</p>
            </motion.div>

            <motion.div
              className="tmf-eval-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.35 }}
              viewport={{ once: true }}
            >
              <svg className="tmf-eval-icon" viewBox="0 0 24 24" fill="none">
                <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 16l4-8 4 4 4-12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h4>Data Depth</h4>
              <p>Connect prompts to mobility & independence</p>
            </motion.div>

            <motion.div
              className="tmf-eval-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <svg className="tmf-eval-icon" viewBox="0 0 24 24" fill="none">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h4>Practicality</h4>
              <p>Durable, easy to implement in real pantry context</p>
            </motion.div>

            <motion.div
              className="tmf-eval-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.45 }}
              viewport={{ once: true }}
            >
              <svg className="tmf-eval-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 2a9 9 0 0 0-9 9c0 5.25 9 13 9 13s9-7.75 9-13a9 9 0 0 0-9-9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 11h8M12 8v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <h4>Sustainability</h4>
              <p>Social-embed tools they can use long term, creating good social impact</p>
            </motion.div>
          </div>

          <p className="tmf-evaluation-conclusion">
            <strong>Eight criteria emerged from their feedback.</strong> Each one became a filter. A lens. A way to evaluate whether a method would truly work in this environment. Not in theory. In practice.
          </p>
        </motion.div>

        {/* Method Selection Animation */}
        <motion.div
          className="tmf-methods-selection"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <h3 className="tmf-narrowing-title">From 15 Ideas to 3 Methods</h3>
          <p className="tmf-narrowing-text">
            We listened. We narrowed. Three methods survived the criteria.
          </p>

          {/* Floating Bubbles Container */}
          <div className="tmf-bubbles-container">
            {/* Background bubbles (12 that fade out) */}
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={`bg-bubble-${i}`}
                className="tmf-method-bubble tmf-bubble-background"
                initial={{
                  opacity: 0.6,
                  scale: 0.8,
                  x: (i % 4 - 1.5) * 100,
                  y: Math.floor(i / 4) * 80 - 80
                }}
                whileInView={{
                  opacity: 0,
                  scale: 0.3,
                  transition: { duration: 1.8, delay: 0.5 + i * 0.08 }
                }}
                viewport={{ once: true }}
              />
            ))}

            {/* The 3 selected methods */}
            <motion.div
              className="tmf-method-bubble tmf-bubble-selected"
              initial={{ opacity: 0, scale: 0.5, y: -60, x: -150 }}
              whileInView={{
                opacity: 1,
                scale: 1,
                y: 0,
                x: -200,
                transition: { duration: 1.2, delay: 2.5, ease: "easeOut" }
              }}
              viewport={{ once: true }}
            >
              <span className="tmf-bubble-label">Rapid Interviews</span>
            </motion.div>

            <motion.div
              className="tmf-method-bubble tmf-bubble-selected"
              initial={{ opacity: 0, scale: 0.5, y: -60, x: 0 }}
              whileInView={{
                opacity: 1,
                scale: 1,
                y: 0,
                x: 0,
                transition: { duration: 1.2, delay: 2.8, ease: "easeOut" }
              }}
              viewport={{ once: true }}
            >
              <span className="tmf-bubble-label">On-Site Engagement</span>
            </motion.div>

            <motion.div
              className="tmf-method-bubble tmf-bubble-selected"
              initial={{ opacity: 0, scale: 0.5, y: -60, x: 150 }}
              whileInView={{
                opacity: 1,
                scale: 1,
                y: 0,
                x: 200,
                transition: { duration: 1.2, delay: 3.1, ease: "easeOut" }
              }}
              viewport={{ once: true }}
            >
              <span className="tmf-bubble-label">Focus Groups</span>
            </motion.div>
          </div>
        </motion.div>

        {/* The Three Methods */}
        <div className="tmf-methods-grid">
          {/* Method 1: AI Interviews - Split Media Layout */}
          <motion.div
            className="tmf-method-card tmf-method-card-1"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="tmf-method-header">
              <h4>AI Moderated Rapid Interviews</h4>
              <div className="tmf-method-stats">
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">50</div>
                  <div className="tmf-stat-label">Participants</div>
                </div>
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">10-15min</div>
                  <div className="tmf-stat-label">Duration</div>
                </div>
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">Car Pickup</div>
                  <div className="tmf-stat-label">Location</div>
                </div>
              </div>
            </div>

            <div className="tmf-method-split">
              <div className="tmf-method-media-col">
                <div className="tmf-method-image">
                  <img
                    src="/content/tmf data/images/ai interview/Frame 304.png"
                    alt="AI moderated interview interface"
                  />
                </div>
                <div className="tmf-method-image">
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  >
                    <source src="https://pub-8a575460bea442e2a9d816d772948be2.r2.dev/tmf-ai-interview.mov" type="video/mp4" />
                  </video>
                </div>
              </div>

              <div className="tmf-method-text-col">
                <div className="tmf-method-block">
                  <h5>What It Is</h5>
                  <p>
                    Audio-based interviews conducted via tablet using the Genway AI platform. Volunteers provide a QR code to participants waiting in their cars at the driveway during online food pickup. Once launched, the AI conducts the entire 10-15 minute conversation autonomously. No human facilitator present. The warm, neighborly tone makes it accessible to those with limited literacy or digital experience. The interview uses semi-structured scripts with conditional branching: limited follow-ups for general awareness, targeted problem-focused probes for those who know about services, and speculative micro-prompts to help participants imagine "best self" scenarios.
                  </p>
                </div>

                <div className="tmf-method-block">
                  <h5>Research Focus</h5>
                  <p>
                    How do pantry clients become aware of, access, and experience SECS's non-food support programs? Map how clients learn about services beyond the food pantry. Identify both practical barriers (transport, childcare, finances, eligibility) and emotional barriers (confidence, motivation, trust) to engagement. Discover small, realistic improvements that could help clients feel more stable or confident. Test whether AI can serve as a respectful, low-barrier tool for gathering input on sensitive topics by removing human intimidation while maintaining empathy.
                  </p>
                </div>
              </div>
            </div>

            <div className="tmf-method-why">
              <svg className="tmf-why-icon" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#FDD62A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p><strong>Why it works:</strong> Removes human intimidation factor. Warm, neighborly tone accessible to those with limited literacy. Meets people exactly where they are.</p>
            </div>
          </motion.div>

          {/* Method 2: Sticker Wall - Bento Grid Layout */}
          <motion.div
            className="tmf-method-card tmf-method-card-2"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="tmf-method-header">
              <h4>Sticker Wall & Heart Wall</h4>
              <div className="tmf-method-stats">
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">300+</div>
                  <div className="tmf-stat-label">Participants</div>
                </div>
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">3-5min</div>
                  <div className="tmf-stat-label">Duration</div>
                </div>
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">Queue Line</div>
                  <div className="tmf-stat-label">Location</div>
                </div>
              </div>
            </div>

            <div className="tmf-method-bento">
              <div className="tmf-bento-images-col">
                <div className="tmf-bento-large">
                  <img
                    src="/content/tmf data/images/sticker wall/Frame 303.png"
                    alt="Sticker wall prototype design"
                  />
                </div>

                <div className="tmf-bento-images-row">
                  <div className="tmf-bento-small">
                    <img
                      src="/content/tmf data/images/sticker wall/PXL_20251015_142105158.RAW-01.MP.COVER.jpg"
                      alt="Sticker wall in use at pantry"
                    />
                  </div>

                  <div className="tmf-bento-small">
                    <img
                      src="/content/tmf data/images/sticker wall/Capstone Fall 2025.png"
                      alt="Sticker wall capstone presentation"
                    />
                  </div>
                </div>
              </div>

              <div className="tmf-bento-content">
                <div className="tmf-method-block">
                  <h5>What It Is</h5>
                  <p>
                    Four large-format physical interactive poster boards (approximately 3' x 4' each) mounted sequentially along the walk-in queue pathway. As neighbors naturally progress through the line, they engage with weather-resistant laminated boards using colorful adhesive dot stickers for responses, star-shaped stickers for priority ratings, and optional sticky notes for open-ended answers. The design is intentionally visual-heavy with a personified character that guides participants through the experience, bilingual content (English and Spanish), and discrete response options to minimize cognitive load. Participation is passive and self-paced. No active recruitment needed, just staff available for assistance. Walls are photographed daily to capture aggregate data patterns, then reset periodically while stickers remain visible for 2-3 days to show accumulating participation.
                  </p>
                </div>
              </div>
            </div>

            <div className="tmf-bento-bottom-row">
              <div className="tmf-method-block">
                <h5>Sequential Wall Experience</h5>
                <div className="tmf-walls-list">
                  <div className="tmf-wall-item">
                    <span className="tmf-wall-number">1</span>
                    <span><strong>Who We Are:</strong> Introduces SECS as more than a food pantry. A community hub offering education, income, and career support through a friendly illustrated map format</span>
                  </div>
                  <div className="tmf-wall-item">
                    <span className="tmf-wall-number">2</span>
                    <span><strong>Who We Can Be in Your Journey:</strong> Participants choose relationship roles (Teammate, Wise Guide, Helpful Neighbor, Caring Friend, Bridge Builder) reflecting how they want SECS to support them</span>
                  </div>
                  <div className="tmf-wall-item">
                    <span className="tmf-wall-number">3</span>
                    <span><strong>What Support Matters Most:</strong> Radial layout where people place stickers on service categories to show what they value or need urgently</span>
                  </div>
                  <div className="tmf-wall-item">
                    <span className="tmf-wall-number">4</span>
                    <span><strong>Help Us Improve:</strong> Explores awareness of non-food services and why neighbors haven't used them, uncovering both awareness and utilization barriers</span>
                  </div>
                </div>
              </div>

              <div className="tmf-method-block">
                <h5>Research Focus</h5>
                <p>
                  Why do neighbors primarily perceive SECS as a food pantry rather than a broader support hub, and what kinds of support do they most value? Understand both practical (time, transportation, eligibility, cost) and emotional forms of support they seek. Explore why SECS is still perceived mainly as a food pantry by examining first-touch experiences, habits, and limited awareness. Investigate why awareness doesn't always lead to utilization by identifying experiential barriers like trust, comfort, motivation, and past experiences. Discover what neighbors believe SECS could become in their personal journey and which support roles best represent how they want help.
                </p>
              </div>
            </div>

            <div className="tmf-method-feature">
              <div className="tmf-feature-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="#FDD62A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p>
                The <strong>Thanksgiving Heart Wall</strong> created a quick, anonymous, reflective space where neighbors shared personal gratitude, hopes, or wishes on colorful cards. This helped us understand their deeper aspirations and emotional experiences beyond daily needs. Unlike the main sticker walls focused on awareness and service needs, this wall invited neighbors to express what matters most to them as people, fostering connection and belonging. Participants picked small square cards and responded to prompts like "A note to my best self..." or "I'm thankful for..." then folded their cards and clipped them to the heart mosaic wall, forming a shared visual of warmth and gratitude. A collective artwork of community emotion.
              </p>
            </div>

            <div className="tmf-method-why">
              <svg className="tmf-why-icon" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#FDD62A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p><strong>Why it works:</strong> Passive engagement. Visual-heavy design with personified characters. Accessible to people with limited literacy. Turned waiting time into contribution.</p>
            </div>
          </motion.div>

          {/* Method 3: Focus Groups - Metaphor-Based Layout */}
          <motion.div
            className="tmf-method-card tmf-method-card-3"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="tmf-method-header">
              <h4>Focus Groups: The Insight Box</h4>
              <div className="tmf-method-stats">
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">4-8</div>
                  <div className="tmf-stat-label">Per Session</div>
                </div>
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">90-120min</div>
                  <div className="tmf-stat-label">Duration</div>
                </div>
                <div className="tmf-method-stat">
                  <div className="tmf-stat-value">Dinner Table</div>
                  <div className="tmf-stat-label">environment</div>
                </div>
              </div>
            </div>

            <div className="tmf-method-metaphor">
              <div className="tmf-metaphor-visual">
                <div className="tmf-method-image">
                  <img
                    src="/content/tmf data/images/insight box/PXL_20251013_152717152.RAW-01.MP.COVER.jpg"
                    alt="Insight box with materials"
                  />
                </div>
                <div className="tmf-method-image">
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  >
                    <source src="https://pub-8a575460bea442e2a9d816d772948be2.r2.dev/tmf-insight-box.mov" type="video/mp4" />
                  </video>
                </div>
              </div>

              <div className="tmf-metaphor-content">
                <div className="tmf-central-prompt">
                  <p className="tmf-prompt-text">
                    "Describe your ideal home meal. Where would it be, and with whom?"
                  </p>
                  <span className="tmf-prompt-label">The Central Metaphor</span>
                </div>

                <div className="tmf-method-block">
                  <h5>What It Is</h5>
                  <p>
                    On-site focus group sessions held around a rectangle table mimicking a dinner table setting. The pantry-box-inspired white Insight Box contains tactile stimulus objects: plates, utensils, cups, drawing materials that participants can share, hold, draw on, or write on. The tactile activity functions as a collective metaphor, inviting each participant to share their ideal home meal while others respond and build on ideas. This semi-structured format fosters trust and equal participation, encouraging cross-talk within a guided group discussion. The meal metaphor serves as a gentle entry point to surface group-level barriers and insights that direct questioning might not reveal.
                  </p>
                </div>
              </div>
            </div>

            <div className="tmf-method-block">
              <h5>Research Focus</h5>
              <p>
                Better understand how SECS's services align with participants' needs and envision possibilities for improvement. Understand what external forces (transport, childcare, finances) and internal forces (confidence, motivation) influence engagement with SECS services by identifying both supportive and hindering factors. Learn what living your best life looks like, how SECS's current services and coaching programs support users, and what might help them get there. Use metaphor-based questioning to uncover aspirations and challenges that may not surface through direct questions, while observing how participants build on each other's ideas to reveal convergent and divergent themes.
              </p>
            </div>

            <div className="tmf-method-why">
              <svg className="tmf-why-icon" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#FDD62A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p><strong>Why it works:</strong> Metaphor-based approach is less direct, more honest. Tactile activity lowers barriers. Group dialogue builds on itself, creating richer insights for people ready to share.</p>
            </div>
          </motion.div>
        </div>

        {/* Prototyping & Iteration Section */}
        <motion.div
          className="tmf-prototyping-section green-accent"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="tmf-prototyping-title">Iteration is the Key</h3>
          <p className="tmf-prototyping-intro">
            Having three methods was just the beginning. We needed to prototype, test, and refine each one to ensure they would actually work in the real environment. We built physical prototypes, tested with SECS staff, and iterated based on their feedback across seven critical criteria: accessibility, emotional safety, engagement, time fit, clarity, data depth, and practicality.
          </p>

          {/* Prototyping Images Grid */}
          <div className="tmf-prototyping-grid">
            <motion.div
              className="tmf-prototype-image"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div>
                <img
                  src="/content/tmf data/images/revised img/WhatsApp Image 2025-11-10 at 13.34.06.jpeg"
                  alt="Preparing stickers and notes for sticker wall"
                />
              </div>
              <p className="tmf-prototype-caption">Sticker wall prep: Organizing stickers, prompts, and visual elements for the wall</p>
            </motion.div>

            <motion.div
              className="tmf-prototype-image"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div>
                <img
                  src="/content/tmf data/images/revised img/IMG_2066.jpg"
                  alt="Measuring wall dimensions at SECS pantry"
                />
              </div>
              <p className="tmf-prototype-caption">Site measurements: Capturing exact wall dimensions for accurate print scaling</p>
            </motion.div>

            <motion.div
              className="tmf-prototype-image"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div>
                <img
                  src="/content/tmf data/images/revised img/IMG_8168.jpg"
                  alt="Insight Box prototype with materials"
                />
              </div>
              <p className="tmf-prototype-caption">Insight Box prototype: Testing meal metaphor with plates, utensils, and prompts</p>
            </motion.div>

            <motion.div
              className="tmf-prototype-image"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div>
                <img
                  src="/content/tmf data/images/revised img/IMG_9050 2.jpg"
                  alt="Feedback notes on prototype wall"
                />
              </div>
              <p className="tmf-prototype-caption">Internal feedback: Team annotations directly on the wall prototype for iteration</p>
            </motion.div>

            <motion.div
              className="tmf-prototype-image"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div>
                <img
                  src="/content/tmf data/images/revised img/IMG_2101.JPG"
                  alt="AI model testing with external users"
                />
              </div>
              <p className="tmf-prototype-caption">Unbiased testing: Validating AI interview with participants unfamiliar with the project</p>
            </motion.div>

            <motion.div
              className="tmf-prototype-image"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <div>
                <img
                  src="/content/tmf data/images/revised img/Capstone Fall 2025.png"
                  alt="Material sourcing for Insight Box prototype"
                />
              </div>
              <p className="tmf-prototype-caption">Material exploration: Testing objects from Amazon, hobby shops, Walmart, and Target for the Insight Box</p>
            </motion.div>
          </div>

          {/* Key Refinements */}
          <div className="tmf-key-refinements">
            <h4>What Changed Through Iteration</h4>
            <div className="tmf-refinements-bento">
              <motion.div
                className="tmf-refinement-item tmf-refinement-ai"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="tmf-refinement-label">AI Interview</div>
                <p>The founder of Genway himself said what we were doing was novel. We removed the email requirement at the start to eliminate friction. Added automatic multilingual support that detects the user's device language without any setup needed. Built in smooth handoffs between questions so it flows naturally. Added moments of thankfulness throughout so it feels like a friendly conversation, not an interview. Carefully matched the interview length to the typical car pickup wait time.</p>
              </motion.div>

              <motion.div
                className="tmf-refinement-item tmf-refinement-sticker"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="tmf-refinement-label">Sticker Wall</div>
                <p>Adjusted the size and positioning of the boards to match the average height of pantry users for comfortable interaction. Went through five design iterations to simplify the flow and reduce cognitive load. Added encouraging side instructions like "one last more" and "you are doing great" to guide users through the experience. The Thanksgiving wall showcased actual quote examples so users immediately understood what to do. Made questions as simple and clear as possible.</p>
              </motion.div>

              <motion.div
                className="tmf-refinement-item tmf-refinement-insight"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="tmf-refinement-label">Insight Box</div>
                <p>Simplified from complex art activity to meal metaphor. Added tactile white objects (plates, utensils) as conversation starters. Created clear facilitation guide to ensure questions stayed light and voluntary, not therapy-like. Designed for easy setup and cleanup.</p>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Trauma-Informed Principles */}
        <motion.div
          className="tmf-trauma-section green-accent"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="tmf-trauma-header">
            <h3>Trauma Informed Principles</h3>
            <p className="tmf-trauma-intro">Every method was designed around these non negotiables:</p>
          </div>

          <div className="tmf-trauma-grid">
            <motion.div
              className="tmf-trauma-card"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="tmf-trauma-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h4>Anonymous by Design</h4>
              <p>No personally identifiable information. Complete privacy protection from the start.</p>
            </motion.div>

            <motion.div
              className="tmf-trauma-card"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="tmf-trauma-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h4>Always Voluntary</h4>
              <p>Verbal consent required. Can opt out anytime. No one trapped in participation.</p>
            </motion.div>

            <motion.div
              className="tmf-trauma-card"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="tmf-trauma-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 10h8M8 14h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h4>Accessible Design</h4>
              <p>Simple language. Visual heavy. Multilingual where possible. Built for everyone.</p>
            </motion.div>

            <motion.div
              className="tmf-trauma-card"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="tmf-trauma-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h4>Meet People Where They Are</h4>
              <p>In their car. In the queue. At a table. Not in a sterile research lab.</p>
            </motion.div>
          </div>

          <motion.p
            className="tmf-trauma-conclusion"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            These weren't just "nice to haves." They were the foundation. Research that respects dignity doesn't extract, it invites.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default MethodsSection;
